import React, { useMemo } from "react";
import { Paper, Text, Badge, Stack } from "@mantine/core";
import { Calendar } from "@mantine/dates";
import { JourneyItem } from "./journey.types";
import { IconCalendar } from "@tabler/icons-react";

interface Props {
  journeys: JourneyItem[];
  onDateSelect: (date: Date) => void;
}

type DateType =
  | "present"
  | "leave"
  | "holiday"
  | "hold"
  | "unmarked"
  | "tool_not_started"
  | "not_schedule"; 

const toKey = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const JourneyCalendar = ({ journeys, onDateSelect }: Props) => {
  const dateMap = useMemo(() => {
    const map = new Map<string, DateType>();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Filter to only MARKED journeys
    const markedJourneys = journeys.filter((j) => j.is_marked);

    const sorted = [...markedJourneys].sort(
      (a, b) =>
        new Date(a.journeyDate).getTime() - new Date(b.journeyDate).getTime(),
    );

    if (sorted.length === 0) return map;

    // Mark every MARKED journey that exists
    sorted.forEach((j) => {
      const key = toKey(new Date(j.journeyDate));

      // PRIMARY CHECK: changeSummary-based detection (most reliable)
      const summary = (j.changeSummary || "").toLowerCase();
      if (summary === "not started") {
        map.set(key, "tool_not_started");
        return;
      }
      if (summary === "not schedule") {
        map.set(key, "not_schedule");
        return;
      }
      if (summary.includes("holiday")) {
        map.set(key, "holiday");
        return;
      }
      if (summary.includes("on leave")) {
        map.set(key, "leave");
        return;
      }
      if (summary.includes("hold") || summary.includes("on hold")) {
        map.set(key, "hold");
        return;
      }

      // FALLBACK CHECK: Look at current trainer's attendance record
      const trainerEmail = (j.trainerEmail || "").toLowerCase().trim();
      if (trainerEmail) {
        const currentTrainer = j.instructors?.find(
          (i: any) => (i.email || "").toLowerCase().trim() === trainerEmail,
        );
        const trainerAttendance = currentTrainer?.attendance || "";

        if (trainerAttendance === "Not Started") {
          map.set(key, "tool_not_started");
          return;
        }
        if (trainerAttendance === "Not Schedule") {
  map.set(key, "not_schedule");
  return;
}
        if (trainerAttendance === "Leave") {
          map.set(key, "leave");
          return;
        }
        if (trainerAttendance === "Holiday") {
          map.set(key, "holiday");
          return;
        }
      }

      // Default: class happened — Present (green)
      map.set(key, "present");
    });

    // Fill gaps from first MARKED journey → today as "unmarked"
    // Sundays in the gap automatically get "holiday" (blue) UNLESS already marked
    if (sorted.length > 0) {
      const start = new Date(sorted[0].journeyDate);
      start.setHours(0, 0, 0, 0);

      for (let d = new Date(start); d <= today; d.setDate(d.getDate() + 1)) {
        const key = toKey(d);
        if (map.has(key)) continue; // already marked (e.g. hold, present, leave)

        // Sundays → auto holiday (blue)
        if (d.getDay() === 0) {
          map.set(key, "holiday");
        } else {
          map.set(key, "unmarked");
        }
      }
    }

    return map;
  }, [journeys]);

  // Statistics
  const stats = useMemo(() => {
    const c = {
      present: 0,
      leave: 0,
      holiday: 0,
      hold: 0,
      unmarked: 0,
      tool_not_started: 0,
      not_schedule: 0, 
    };
    dateMap.forEach((t) => {
      if (t in c) c[t as keyof typeof c]++;
    });
    return c;
  }, [dateMap]);

  // Color palette for each date type
  const colors: Record<DateType, { bg: string; border?: string }> = {
    present: { bg: "#4CAF50" }, // Green
    leave: { bg: "#FF9800" }, // Orange
    holiday: { bg: "#2196F3" }, // Blue
    hold: { bg: "#E11D48" }, // Rose — distinct from Leave orange
    tool_not_started: { bg: "#9C27B0" }, // Purple
     not_schedule: { bg: "#6366F1" }, 
    unmarked: { bg: "#9E9E9E", border: "2px solid #666" }, // Grey
  };

  const getDayProps = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const check = new Date(date);
    check.setHours(0, 0, 0, 0);

    // Future dates → disabled UNLESS they have a journey entry (e.g. hold)
    if (check > today) {
      const key = toKey(check);
      const type = dateMap.get(key);

      // If future date has a journey entry, show it with its color (clickable)
      if (type && colors[type]) {
        const { bg, border } = colors[type];
        return {
          disabled: false,
          onClick: () => onDateSelect(check),
          style: {
            backgroundColor: bg,
            color: "#fff",
            fontWeight: 700,
            borderRadius: "50%",
            cursor: "pointer",
            border: border || "none",
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0.75, // slightly dimmed to indicate future
          } as React.CSSProperties,
        };
      }

      // No entry → disabled
      return {
        disabled: true,
        style: {
          cursor: "not-allowed",
          color: "#ccc",
          backgroundColor: "transparent",
        } as React.CSSProperties,
      };
    }

    const key = toKey(check);
    const type = dateMap.get(key);

    if (type && colors[type]) {
      const { bg, border } = colors[type];
      return {
        disabled: false,
        onClick: () => onDateSelect(check),
        style: {
          backgroundColor: bg,
          color: "#fff",
          fontWeight: 700,
          borderRadius: "50%",
          cursor: "pointer",
          border: border || "none",
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        } as React.CSSProperties,
      };
    }

    // Plain past date (before batch started) — reset Mantine's default Sunday/today blue
    return {
      disabled: false,
      onClick: () => onDateSelect(check),
      style: {
        cursor: "pointer",
        borderRadius: "50%",
        width: 36,
        height: 36,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Override any Mantine default background (Sunday blue, today highlight)
        backgroundColor: "transparent",
        color: "inherit",
      } as React.CSSProperties,
    };
  };

  return (
    <Paper
      shadow="md"
      radius="lg"
      className="p-4 border border-[#FC7A69]/20 w-full overflow-hidden"
    >
      {/* Title */}
      <div className="flex items-center gap-2 mb-3">
        <IconCalendar size={20} className="text-[#FC7A69]" />
        <Text className="text-sm font-bold text-[#221E45]">
          Journey Calendar
        </Text>
      </div>

      {/* Calendar */}
      <div className="w-full overflow-x-hidden">
        <Calendar
          size="md"
          getDayProps={getDayProps}
          styles={{
            day: {
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.8rem",
              // Override Mantine's default [data-weekend] blue color for Sunday/Saturday
              "&[data-weekend]": {
                color: "inherit",
              },
              // Override Mantine's today highlight
              "&[data-today]": {
                fontWeight: "bold",
                backgroundColor: "transparent",
              },
              // Override Mantine's selected date highlight
              "&[data-selected]": {
                backgroundColor: "transparent",
              },
            },
            weekday: {
              fontSize: "0.7rem",
              fontWeight: 600,
            },
            calendarHeader: {
              marginBottom: 8,
            },
            calendarHeaderLevel: {
              fontSize: "0.9rem",
              fontWeight: 700,
            },
            calendarHeaderControl: {
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
            calendarHeaderControlIcon: {
              width: 14,
              height: 14,
            },
          }}
        />
      </div>

      {/* Legend with proper colors */}
      <Stack gap="xs" className="mt-4">
        <LegendRow color="#4CAF50" label="Present" count={stats.present} />
        <LegendRow color="#FF9800" label="Leave" count={stats.leave} />
        <LegendRow color="#2196F3" label="Holiday" count={stats.holiday} />
        <LegendRow
          color="#9C27B0"
          label="Not Started"
          count={stats.tool_not_started}
        />
        <LegendRow
  color="#6366F1"
  label="Not Schedule"
  count={stats.not_schedule}
/>
        <LegendRow color="#E11D48" label="Hold" count={stats.hold} />
        <LegendRow
          color="#9E9E9E"
          label="Unmarked"
          count={stats.unmarked}
          border="2px solid #666"
          bold
        />
        <div className="flex items-center justify-between opacity-50">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#ccc]" />
            <Text className="text-xs text-[#221E45]/70">Future (Disabled)</Text>
          </div>
        </div>
      </Stack>
    </Paper>
  );
};

function LegendRow({
  color,
  label,
  count,
  border,
  bold,
}: {
  color: string;
  label: string;
  count: number;
  border?: string;
  bold?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: color, border: border || "none" }}
        />
        <Text
          className={`text-xs text-[#221E45]/70 ${bold ? "font-semibold" : ""}`}
        >
          {label}
        </Text>
      </div>
      <Badge size="xs" style={{ backgroundColor: `${color}20`, color }}>
        {count}
      </Badge>
    </div>
  );
}

export default JourneyCalendar;
